import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace("://www.", "://");

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ht_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// mandatory-MFA lockout signal: any 403 MFA_SETUP_REQUIRED anywhere in the app tells
// the Control Tower to show the setup screen instead of generic load errors
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 403 && typeof detail === "string" && detail.startsWith("MFA_SETUP_REQUIRED")) {
      window.dispatchEvent(new Event("ht:mfa-required"));
    }
    return Promise.reject(err);
  }
);

export async function uploadFile(file, kind = "document") {
  const fd = new FormData();
  fd.append("file", file);
  const headers = {};
  const token = localStorage.getItem("ht_token");
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}/upload?kind=${kind}`, {
    method: "POST",
    headers,
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === "string" ? err.detail : "Upload failed. Please try again.");
  }
  return res.json();
}

export function fileUrl(path) {
  const token = localStorage.getItem("ht_token");
  return `${API}/files/${path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
}

export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatSalary(job) {
  if (!job || !job.show_salary || (!job.salary_min && !job.salary_max)) return null;
  const fmt = (n) => `£${Math.round(Number(n)).toLocaleString("en-GB")}`;
  let period =
    { year: "a year", month: "a month", week: "a week", day: "a day", hour: "an hour" }[job.salary_period] || "";
  // partner feeds sometimes carry hourly rates in the annual salary fields (e.g. Reed £13/hour)
  if (period === "a year" && job.salary_max && job.salary_max < 1000) period = "an hour";
  if (job.salary_min && job.salary_max) {
    // collapse identical endpoints (common in partner feeds) to a single figure
    if (Math.round(Number(job.salary_min)) === Math.round(Number(job.salary_max)))
      return `${fmt(job.salary_max)} ${period}`;
    return `${fmt(job.salary_min)} – ${fmt(job.salary_max)} ${period}`;
  }
  return `${fmt(job.salary_min || job.salary_max)} ${period}`;
}

export function timeAgo(dateStr) {
  if (!dateStr) return "";
  const then = new Date(dateStr);
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

export async function streamAi(path, body, onDelta) {
  const headers = { "Content-Type": "application/json" };
  const token = localStorage.getItem("ht_token");
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === "string" ? err.detail : "The AI assistant is busy. Try again in a moment.");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let full = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    full += decoder.decode(value, { stream: true });
    onDelta(full);
  }
  return full;
}
